import React, { useCallback, useMemo, useState } from "react";
import { Alert, Button, Dialog, DialogContent, Grid, lighten, TextField, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ActionState as State, ActionProps as Props } from "./interfaces";
import DialogTitle from "../../../DialogTitle/DialogTitle";
import DialogActions from "../../../DialogActions/DialogActions";
import ConfirmationChangesDialog from "../../../ConfirmationChangesDialog/ConfirmationChangesDialog";
import { SendIncomingMailApiService } from "../../../../services/incoming-mail/send-incoming-mail-api.service";
import LoadingOverlay from "../../../LoadingOverlay/LoadingOverlay";
import { INCOMING_MAIL_STATUSES } from "src/utils/constants/constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { SendIncomingMailFilter } from "src/models/incoming-mail";
const SendIncomingMail = ({ incomingMail, handleNotifyParent }: Props) => {
  const tRP = "components.send_incoming_mail";
  const { t } = useTranslation();
  const theme = useTheme();

  const INITIAL_STATE: State = useMemo(
    () => ({
      open: false,
      loading: false,
      CCDOpen: false,
      error: "",
      subject: incomingMail.subject?.subject ?? null,
      emailBody: `${t(`${tRP}.email_body`)}`,
    }),
    [incomingMail.subject?.subject]
  );

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  const handleSend = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));
    try {
      const response = await new SendIncomingMailApiService().loadItems({
        ids: [incomingMail.id],
        subject: state.subject !== incomingMail.subject?.subject ? state.subject : null,
        emailBody: state.emailBody,
      } as SendIncomingMailFilter);
      if (response.error) {
        setState((state) => ({ ...state, error: response.error }));
      } else {
        setState({ ...INITIAL_STATE });
        handleNotifyParent();
      }
    } catch (error) {
      setState((state) => ({ ...state, error: error as string }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [incomingMail.id, INITIAL_STATE, handleNotifyParent, state.subject, state.emailBody]);

  return (
    <>
      {incomingMail.status?.id === INCOMING_MAIL_STATUSES.Discarded.id ? (
        <Typography
          style={{ color: theme.palette.secondary.light }}
          title={`${t(`${tRP}.not_allowed_to_send_discarded`)}`}
        >{`${t(`${tRP}.button_title`)}`}</Typography>
      ) : !incomingMail.addressedTo ? (
        <Typography
          style={{ color: theme.palette.secondary.light }}
          title={`${t(`${tRP}.not_allowed_no_addressee`)}`}
        >
          {`${t(`${tRP}.button_title`)}`}&nbsp;
          <FontAwesomeIcon icon={faCircleExclamation} />
        </Typography>
      ) : (
        <Button
          onClick={() => setState((state) => ({ ...state, open: true }))}
          style={{ color: theme.palette.text.primary }}
        >
          {incomingMail.status?.id === INCOMING_MAIL_STATUSES.Emailed.id
            ? `${t(`${tRP}.button_re_title`)}`
            : `${t(`${tRP}.button_title`)}`}
        </Button>
      )}
      <Dialog open={state.open}>
        <DialogTitle
          title={`${t(`${tRP}.title`)}`}
          handleClose={() => setState((state) => ({ ...state, CCDOpen: true }))}
        />
        <DialogContent>
          <LoadingOverlay
            error={state.error}
            loading={state.loading}
          >
            {`${t(`${tRP}.info`)}`}

            <Grid
              style={{
                padding: "15px",
                border: `1px solid ${theme.palette.secondary.light}`,
                borderRadius: "5px",
                display: "flex",
                flexDirection: "column",
                gap: "10px",
              }}
            >
              <Grid
                style={{
                  display: "flex",
                  gap: "20px",
                  padding: "15px",
                  border: `1px solid ${lighten(theme.palette.secondary.light, 0.8)}`,
                  borderRadius: "5px",
                }}
              >
                <Typography style={{ width: "48px" }}>{`${t(`${tRP}.to`)}`} :</Typography>
                <Grid>
                  {incomingMail.addressedTo?.recipients.map((recipient) => (
                    <Typography variant="body2">{recipient.user?.email}</Typography>
                  ))}
                </Grid>
                {incomingMail.addressedTo?.recipients.length === 0 && (
                  <Alert severity="error">{`${t(`${tRP}.no_recipients`)}`}</Alert>
                )}
              </Grid>
              <Grid
                style={{
                  padding: "15px",
                  border: `1px solid ${lighten(theme.palette.secondary.light, 0.8)}`,
                  borderRadius: "5px",
                  alignItems: "center",
                  display: "flex",
                  gap: "10px",
                }}
              >
                {`${t(`${tRP}.subject`)}`} :
                <TextField
                  value={state.subject}
                  onChange={({ currentTarget: { value } }) =>
                    setState((state) => ({
                      ...state,
                      subject: value,
                    }))
                  }
                />
              </Grid>
              <TextField
                value={state.emailBody}
                onChange={({ currentTarget: { value } }) =>
                  setState((state) => ({
                    ...state,
                    emailBody: value ?? "",
                  }))
                }
              />
            </Grid>
          </LoadingOverlay>
        </DialogContent>
        <DialogActions
          handleCancel={() => setState((state) => ({ ...state, CCDOpen: true }))}
          handleSave={handleSend}
          customSaveName={`${t(`${tRP}.yes_send`)}`}
          disabledSaveButton={incomingMail.addressedTo?.recipients.length === 0}
        />
      </Dialog>
      <ConfirmationChangesDialog
        open={state.CCDOpen}
        handleCancel={() => setState((state) => ({ ...state, CCDOpen: false }))}
        handleDiscard={() => setState((state) => ({ ...state, CCDOpen: false, open: false }))}
      />
    </>
  );
};

export default SendIncomingMail;
